import { Box, Button, Field, Input, Stack, Text, VStack } from "@chakra-ui/react"
import { useState, useCallback, useEffect } from "react"
import { authClient } from "./auth"
import { useNavigate, useSearchParams } from "react-router-dom"
import Link from "@hydrologiq/shared/components/Link"

export function ResetPasswordForm() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [token, setToken] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)

  // Extract token from URL on mount
  useEffect(() => {
    const tokenFromUrl = searchParams.get("token")
    if (tokenFromUrl) {
      setToken(tokenFromUrl)
    } else {
      setError("Invalid or missing reset token")
    }
  }, [searchParams])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setError(null)
      setSuccess(false)

      // Validation
      if (!token) {
        setError("Invalid reset token")
        return
      }

      if (!password || !confirmPassword) {
        setError("All fields are required")
        return
      }

      if (password.length < 8) {
        setError("Password must be at least 8 characters")
        return
      }

      if (password !== confirmPassword) {
        setError("Passwords do not match")
        return
      }

      setLoading(true)

      await authClient
        .resetPassword({
          newPassword: password,
          token,
        })
        .then(() => {
          setSuccess(true)
          setTimeout(() => {
            navigate("/auth/sign-in")
          }, 2000)
        })
        .catch((err: any) => {
          setError(err.message || "Failed to reset password. The link may have expired.")
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [token, password, confirmPassword, navigate]
  )

  return (
    <Box
      as="form"
      onSubmit={handleSubmit}
      w="full"
      maxW="md"
      bg="card"
      p={8}
      borderRadius="md"
      boxShadow="md"
      color="primary"
    >
      <VStack gap={6} align="stretch">
        <VStack gap={2} align="stretch">
          <Text fontSize="2xl" fontWeight="bold">
            Set New Password
          </Text>
          <Text fontSize="sm">Enter your new password below.</Text>
        </VStack>

        {success ? (
          <VStack gap={4} align="stretch">
            <Box bg="green.50" p={4} borderRadius="md" borderWidth="1px" borderColor="green.200">
              <Text color="green.700" fontSize="sm" fontWeight="medium" mb={2}>
                Password reset successful!
              </Text>
              <Text color="green.700" fontSize="sm">
                Redirecting to sign in...
              </Text>
            </Box>
          </VStack>
        ) : (
          <>
            <Field.Root required>
              <Field.Label>New Password</Field.Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                disabled={loading || !token}
              />
              <Field.HelperText>Must be at least 8 characters</Field.HelperText>
            </Field.Root>

            <Field.Root required>
              <Field.Label>Confirm New Password</Field.Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                disabled={loading || !token}
              />
            </Field.Root>

            {error && (
              <Box bg="red.50" p={3} borderRadius="md" borderWidth="1px" borderColor="red.200">
                <Text color="red.700" fontSize="sm">
                  {error}
                </Text>
              </Box>
            )}

            <Button type="submit" variant="hydrologiq_primary" w="full" disabled={loading || !token}>
              {loading ? "Resetting..." : "Reset Password"}
            </Button>

            <Stack direction="row" justify="center" gap={1} fontSize="sm">
              <Text>Remember your password?</Text>
              <Link href="/auth/sign-in" variant={"primary" as any} internal>
                Sign in
              </Link>
            </Stack>
          </>
        )}
      </VStack>
    </Box>
  )
}
